import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import {
  X, Upload, Loader2, FileText, Check, Trash2, Plus,
  Library, FlaskConical, AlertTriangle, FolderOpen, Sparkles, ChevronDown, ChevronUp, FolderUp, Info,
} from 'lucide-react';
import axiosInstance from '../../api/axios';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import { buildAttributeList } from '../control-new-look/TestAttributesSubTab';
import { aiMapEvidence, saveEvidenceMap } from '../attribute-result/mapEvidence';
import { isPopulationNotRequired, syncEvidenceSamples } from '../../api/controlTestRuns';
import {
  dedupeEvidenceFiles,
  findLabelCollision,
  labelSameNameVersions,
  uniqueSampleLabel,
} from './evidenceKeys';
import { FolderUploadDialog, LibraryPicker } from '../../features/evidence-library/index.ts';
import { linkLibraryFiles } from '../../request/evidence-library/evidenceLibraryRequests.ts';
import { useInvalidateLibrary } from '../../hooks/evidence-library/useEvidenceLibrary.ts';
import { runPool } from '../../features/evidence-library/uploadRunner.ts';
import { useAuditYear } from '../../contexts/AuditYearContext.tsx';

/* ── Category config ──────────────────────────────────────────────────────── */
const CATEGORIES = {
  population: { label: 'Population', badgeCls: 'bg-blue-100 text-blue-800 border-blue-300', borderCls: 'border-blue-300 bg-blue-50/30', dotCls: 'bg-blue-500', description: 'Complete transaction listing for the testing period' },
  ipe_proof:  { label: 'IPE Proof',  badgeCls: 'bg-purple-100 text-purple-800 border-purple-300', borderCls: 'border-purple-300 bg-purple-50/30', dotCls: 'bg-purple-500', description: 'Independent source to verify population completeness' },
  evidence:   { label: 'Evidence',   badgeCls: 'bg-teal-100 text-teal-800 border-teal-300', borderCls: 'border-teal-300 bg-teal-50/30', dotCls: 'bg-teal-500', description: 'Supporting document for a sample item (PBC)' },
  supporting: { label: 'Supporting', badgeCls: 'bg-gray-100 text-gray-700 border-gray-300', borderCls: 'border-gray-300 bg-gray-50/30', dotCls: 'bg-gray-400', description: 'Policy, procedure or walkthrough — kept with the control, not tested' },
};
const CATEGORY_ORDER = ['population', 'ipe_proof', 'evidence', 'supporting'];

function CategoryPickerModal({ filename, onConfirm, onCancel }) {
  const [picked, setPicked] = useState('evidence');
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-[420px] p-6 space-y-4">
        <h3 className="text-sm font-bold text-gray-900">Categorise file</h3>
        <p className="text-xs text-gray-500 truncate">{filename}</p>
        <div className="space-y-2">
          {CATEGORY_ORDER.map(cat => {
            const cfg = CATEGORIES[cat];
            return (
              <button key={cat} onClick={() => setPicked(cat)}
                className={`w-full flex items-start gap-3 border rounded-lg px-4 py-3 text-left transition-colors ${picked === cat ? cfg.borderCls + ' border-2' : 'border-gray-200 hover:border-gray-300'}`}>
                <span className={`mt-1 h-3 w-3 rounded-full shrink-0 ${cfg.dotCls}`} />
                <div>
                  <p className="text-xs font-semibold text-gray-800">{cfg.label}</p>
                  <p className="text-[11px] text-gray-500">{cfg.description}</p>
                </div>
              </button>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onCancel} className="text-xs text-gray-500 px-3 py-1.5 rounded hover:bg-gray-100">Cancel</button>
          <button onClick={() => onConfirm(picked)} className="text-xs font-semibold text-white bg-indigo-600 px-4 py-1.5 rounded hover:bg-indigo-700">
            Upload as {CATEGORIES[picked].label}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Confidence helpers ───────────────────────────────────────────────────── */
function confLevel(pct) {
  if (pct >= 85) return 'hi';
  if (pct >= 60) return 'mid';
  return 'lo';
}
const CONF_DOT = { hi: 'bg-green-500', mid: 'bg-amber-500', lo: 'bg-red-500' };
const CONF_TEXT = { hi: 'text-green-700', mid: 'text-amber-700', lo: 'text-red-600' };

function ConfDot({ pct }) {
  const lvl = confLevel(pct);
  return <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${CONF_DOT[lvl]}`} />;
}

/* ── Floating layer positioning ───────────────────────────────────────── */
// Popovers inside the scrolling file table get clipped by it, so they render
// into <body> with fixed coordinates. Opens on the preferred side of the
// anchor when it fits, otherwise the other side, and is kept on screen.
function useFixedPosition(open, anchorRef, popRef, prefer = 'below') {
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    if (!open) { setPos(null); return undefined; }
    const place = () => {
      if (!anchorRef.current || !popRef.current) return;
      const r = anchorRef.current.getBoundingClientRect();
      const h = popRef.current.offsetHeight;
      const w = popRef.current.offsetWidth;
      const gap = 8;
      const above = r.top - h - gap;
      const below = r.bottom + gap;
      const fitsAbove = above >= gap;
      const fitsBelow = below + h <= window.innerHeight - gap;
      const useAbove = prefer === 'above' ? (fitsAbove || !fitsBelow) : (!fitsBelow && fitsAbove);
      const top = useAbove ? above : Math.max(gap, Math.min(below, window.innerHeight - h - gap));
      const left = Math.max(gap, Math.min(r.left, window.innerWidth - w - gap));
      setPos({ top, left });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchorRef, popRef, prefer]);
  return pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' };
}

// Closes on a mousedown outside the anchor and the (portalled) popover.
function useDismissOnOutsideClick(open, setOpen, anchorRef, popRef) {
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (anchorRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, setOpen, anchorRef, popRef]);
}

/* ── Step popover on attribute chip ──────────────────────────────────────── */
function AttrChipWithSteps({ attr, state, onToggle, aiConf, aiExcerpt }) {
  // state: 'confirmed' | 'ai' | 'off'
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const popRef = useRef(null);
  const closeTimer = useRef(null);
  const popStyle = useFixedPosition(open, ref, popRef, 'above');
  useDismissOnOutsideClick(open, setOpen, ref, popRef);

  // Short close delay so the pointer can cross from the chip to the popover.
  const show = () => { clearTimeout(closeTimer.current); setOpen(true); };
  const hide = () => { closeTimer.current = setTimeout(() => setOpen(false), 120); };
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const chipCls = state === 'confirmed'
    ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
    : state === 'ai'
      ? 'bg-violet-50 border-violet-400 text-violet-700'
      : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300';

  const steps = attr.steps || attr.procedure_steps || [];
  const hasSteps = steps.length > 0 || attr.description || attr.procedure;

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => onToggle(attr.ref)}
        onMouseEnter={() => hasSteps && show()}
        onMouseLeave={hide}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border transition-colors ${chipCls}`}
      >
        {state === 'confirmed' && <Check className="h-2.5 w-2.5" />}
        {state === 'ai' && <ConfDot pct={aiConf} />}
        {attr.ref}
        {state === 'ai' && (
          <span className={`text-[9px] font-bold ${CONF_TEXT[confLevel(aiConf)]}`}>{aiConf}%</span>
        )}
      </button>

      {open && hasSteps && createPortal(
        <div ref={popRef} className="fixed z-[70] bg-white border border-violet-200 rounded-xl shadow-xl p-3 w-72 text-left"
          style={popStyle}
          onMouseEnter={show} onMouseLeave={hide}>
          <p className="text-[10px] font-bold text-violet-700 uppercase tracking-wide mb-2">
            {attr.ref} — {attr.label || attr.ref}
          </p>
          {steps.length > 0 ? (
            <div className="space-y-1.5 mb-2">
              {steps.map((step, i) => (
                <div key={i} className="flex gap-2">
                  <span className="text-[9px] font-bold text-violet-400 min-w-[14px] pt-0.5">{i + 1}.</span>
                  <span className="text-[10px] text-gray-600 leading-tight">
                    {step.instruction || step.description || step.text || String(step)}
                  </span>
                </div>
              ))}
            </div>
          ) : attr.description ? (
            <p className="text-[10px] text-gray-600 mb-2">{attr.description}</p>
          ) : attr.procedure ? (
            <p className="text-[10px] text-gray-600 mb-2">{attr.procedure}</p>
          ) : null}
          {state === 'ai' && (
            <div className="pt-2 border-t border-violet-100 flex items-start gap-1.5">
              <Sparkles className="h-3 w-3 text-violet-500 shrink-0 mt-0.5" />
              <p className="text-[10px] text-violet-700">
                {aiExcerpt || `AI confidence: ${aiConf}% — click to ${state === 'ai' ? 'accept' : 'remove'}`}
              </p>
            </div>
          )}
          {state === 'off' && (
            <div className="pt-2 border-t border-gray-100">
              <p className="text-[10px] text-gray-400">Click chip to add this attribute manually</p>
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

/* ── Per-file mapping completeness ───────────────────────────────────────── */
// Module-level so SelectedFileRow and ScopePanel can both call it without
// needing it passed as a prop.
function fileIsMapped(ev) {
  return ev._attributes.length > 0 && ev._samples.length > 0;
}

/* ── Selected file table row ──────────────────────────────────────────────── */
// Top-level component so React preserves instance identity across parent
// re-renders — avoids remounting and resetting local dropdown state.
function SelectedFileRow({ ev, sampleLabels, samples, attributes, savingIds, savedIds, aiSugg,
  onAcceptFileSugg, onToggleSample, onSetAllSamples, onToggleSelect, onToggleAttribute }) {
  const [samplesOpen, setSamplesOpen] = useState(false);
  const samplesBtnRef = useRef(null);
  const samplesPopRef = useRef(null);
  const samplesStyle = useFixedPosition(samplesOpen, samplesBtnRef, samplesPopRef, 'below');
  useDismissOnOutsideClick(samplesOpen, setSamplesOpen, samplesBtnRef, samplesPopRef);
  const cat      = ev.file_category || 'evidence';
  const cfg      = CATEGORIES[cat] || CATEGORIES.evidence;
  const isSaving = savingIds.has(ev.id);
  const justSaved= savedIds.has(ev.id);
  const fid      = String(ev.control_upload_id ?? ev.id ?? '');
  const sugg     = cat === 'evidence' ? (aiSugg[fid] || null) : null;
  const mapped   = fileIsMapped(ev);
  const hasSugg  = sugg && (Object.keys(sugg.attrs).length > 0 || Object.keys(sugg.samples).length > 0);

  // Always show SAMPLE-N labels — never raw record_refs.
  // Fall back to position lookup if the ref isn't in the current label map;
  // skip refs that can't be resolved (stale mappings from a different population).
  const selectedSampleLabels = ev._samples
    .map(s => {
      if (sampleLabels[s]) return sampleLabels[s];
      const idx = samples.indexOf(s);
      return idx >= 0 ? `SAMPLE-${idx + 1}` : null;
    })
    .filter(Boolean);
  const noSamplesSelected = cat === 'evidence' && samples.length > 0 && ev._samples.length === 0;
  const btnLabel = selectedSampleLabels.length === 0
    ? 'None'
    : selectedSampleLabels.length === samples.length
      ? 'All'
      : selectedSampleLabels.join(', ');

  return (
    <tr className={`border-b border-gray-100 transition-colors ${mapped ? 'bg-green-50/20' : hasSugg ? 'bg-violet-50/10' : 'hover:bg-gray-50/60'}`}>

      {/* File name — shows type badge for non-evidence files */}
      <td className="px-3 py-2 max-w-[180px]">
        <div className="flex items-center gap-1.5">
          <FileText className="h-3.5 w-3.5 text-gray-300 shrink-0" />
          <div className="min-w-0">
            <span className="text-[11px] text-gray-800 truncate block leading-tight" title={ev.filename}>{ev.filename}</span>
            <div className="flex items-center gap-1 mt-0.5">
              {ev._versionLabel && (
                <span className="text-[9px] font-semibold px-1.5 py-0 rounded-full border border-amber-300 bg-amber-50 text-amber-800" title={ev._versionTitle}>{ev._versionLabel}</span>
              )}
              {cat !== 'evidence' && (
                <span className={`text-[9px] font-semibold px-1.5 py-0 rounded-full border ${cfg.badgeCls}`}>{cfg.label}</span>
              )}
              {isSaving  && <Loader2 className="h-2.5 w-2.5 animate-spin text-gray-400" />}
              {justSaved && <Check   className="h-2.5 w-2.5 text-green-600" />}
              {mapped && !isSaving && !justSaved && <span className="text-[9px] text-green-700 font-semibold">✓ Mapped</span>}
              {hasSugg   && <button onClick={() => onAcceptFileSugg(ev)} className="text-[9px] font-bold text-violet-600 hover:text-violet-800">✦ Accept AI</button>}
            </div>
          </div>
        </div>
      </td>

      {/* Samples multi-select dropdown */}
      <td className="px-2 py-2 relative">
        {cat === 'evidence' && samples.length > 0 ? (
          <div className="relative">
            <button
              ref={samplesBtnRef}
              type="button"
              onClick={() => setSamplesOpen(o => !o)}
              title={noSamplesSelected ? '⚠ No sample selected — this file won\'t be tested' : undefined}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border whitespace-nowrap transition-colors ${
                ev._samples.length > 0
                  ? 'bg-teal-50 border-teal-300 text-teal-700'
                  : 'bg-amber-50 border-amber-300 text-amber-600'
              }`}
            >
              <span>{btnLabel}</span>
              <ChevronDown className="h-2.5 w-2.5 opacity-60" />
            </button>
            {samplesOpen && createPortal(
              <div ref={samplesPopRef} style={samplesStyle}
                className="fixed z-[70] bg-white border border-gray-200 rounded-lg shadow-lg min-w-[160px] max-h-64 overflow-y-auto py-1">
                <label className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-100 hover:bg-gray-50 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    tabIndex={-1}
                    checked={ev._samples.length === samples.length}
                    ref={el => { if (el) el.indeterminate = ev._samples.length > 0 && ev._samples.length < samples.length; }}
                    onChange={() => onSetAllSamples(ev.id, ev._samples.length !== samples.length)}
                    className="accent-teal-600 h-3 w-3"
                  />
                  <span className="text-[11px] font-semibold text-gray-700 flex-1">Select all</span>
                </label>
                {samples.map((sample) => {
                  const checked = ev._samples.includes(sample);
                  const aiConf  = !checked && sugg?.samples[sample];
                  return (
                    <label key={sample}
                      title={sample}
                      className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        tabIndex={-1}
                        checked={checked}
                        onChange={() => onToggleSample(ev.id, sample)}
                        className="accent-teal-600 h-3 w-3"
                      />
                      <span className="text-[11px] text-gray-700 flex-1">{sampleLabels[sample]}</span>
                      {aiConf && <span className="text-[9px] font-bold text-violet-600">{aiConf}%</span>}
                    </label>
                  );
                })}
              </div>,
              document.body,
            )}
          </div>
        ) : (
          <span className="text-gray-200 text-[10px]">—</span>
        )}
      </td>

      {/* Attribute columns — evidence only */}
      {attributes.map(attr => {
        if (cat !== 'evidence') return <td key={attr.ref} className="px-1 py-2 text-center"><span className="text-gray-200 text-[10px]">—</span></td>;
        const confirmed   = ev._attributes.includes(attr.ref);
        const aiSuggested = !confirmed && sugg?.attrs[attr.ref];
        const state       = confirmed ? 'confirmed' : aiSuggested ? 'ai' : 'off';
        return (
          <td key={attr.ref} className="px-1 py-2 text-center">
            <AttrChipWithSteps
              attr={attr}
              state={state}
              onToggle={(ref) => onToggleAttribute(ev.id, ref)}
              aiConf={aiSuggested?.conf}
              aiExcerpt={aiSuggested?.excerpt}
            />
          </td>
        );
      })}

      {/* Remove */}
      <td className="px-2 py-2 text-center">
        <button onClick={() => onToggleSelect(ev.id)} title="Remove from this test" className="text-gray-300 hover:text-red-400">
          <X className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}

/* ── Scope summary panel ──────────────────────────────────────────────────── */
// Top-level so React preserves instance identity — avoids remounting and
// resetting the open/close state on every parent re-render.
function ScopePanel({ selectedEvidenceFiles, sampleLabels, scopeOpen, setScopeOpen }) {
  const mapped = selectedEvidenceFiles.filter(fileIsMapped);
  return (
    <div className="border-t border-gray-200 shrink-0">
      <button onClick={() => setScopeOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-2.5 text-left hover:bg-gray-50">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">🔒 Test Engine Scope</span>
          {mapped.length > 0
            ? <span className="text-[10px] text-green-700 font-semibold">{mapped.length} file{mapped.length !== 1 ? 's' : ''} mapped</span>
            : <span className="text-[10px] text-amber-600 font-semibold">No files fully mapped</span>}
        </div>
        {scopeOpen ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
      </button>
      {scopeOpen && (
        <div className="px-5 pb-3 space-y-2">
          <p className="text-[10px] text-gray-400">Only these files and their confirmed mappings will be sent to the test engine. No other library files or population files are included.</p>
          {mapped.length === 0 && (
            <p className="text-[10px] text-amber-600 italic">Map at least one evidence file to a sample and attribute before running.</p>
          )}
          {mapped.map(ev => (
            <div key={ev.id} className="flex items-start gap-2">
              <span className="text-[10px] text-gray-500 truncate flex-1 min-w-0" title={ev.filename}>📄 {ev.filename}</span>
              <div className="flex items-center gap-1 flex-wrap justify-end">
                {ev._attributes.length > 0 && (
                  <span className="text-[9px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 rounded px-1.5 py-0.5">
                    {ev._attributes.join(' ')}
                  </span>
                )}
                {ev._samples.map(s => (
                  <span key={s} className="text-[9px] font-bold bg-teal-50 text-teal-700 border border-teal-200 rounded px-1.5 py-0.5 max-w-[100px] truncate">{sampleLabels[s] || s}</span>
                ))}
              </div>
            </div>
          ))}
          {selectedEvidenceFiles.filter(e => !fileIsMapped(e)).length > 0 && (
            <div className="text-[10px] text-gray-400 italic">
              {selectedEvidenceFiles.filter(e => !fileIsMapped(e)).length} file{selectedEvidenceFiles.filter(e => !fileIsMapped(e)).length !== 1 ? 's' : ''} incomplete (no attribute or sample) — excluded from test.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Main modal ───────────────────────────────────────────────────────────── */
const ANALYSIS_MODE_KEY = 'controlTest.analysisMode';

function storedAnalysisMode() {
  try {
    return window.localStorage.getItem(ANALYSIS_MODE_KEY) === 'exhaustive' ? 'exhaustive' : 'fast';
  } catch {
    return 'fast';
  }
}

export default function EvidenceUploadModal({ control, controlUuid, run, onClose, onSaved }) {
  // How the AI judges the samples: Fast (one call per sample, all attributes) or Exhaustive (one checking session
  // per sample × attribute). Remembered per browser.
  const [analysisMode, setAnalysisMode] = useState(storedAnalysisMode);
  const chooseAnalysisMode = (mode) => {
    setAnalysisMode(mode);
    try { window.localStorage.setItem(ANALYSIS_MODE_KEY, mode); } catch { /* not remembered */ }
  };
  const [allFiles, setAllFiles]         = useState([]);
  const [selectedIds, setSelectedIds]   = useState(new Set());
  const [loading, setLoading]           = useState(true);
  const [uploading, setUploading]       = useState(false);
  const [savingIds, setSavingIds]       = useState(new Set());
  const [savedIds, setSavedIds]         = useState(new Set());
  const [pickerFile, setPickerFile]     = useState(null);
  const [folderUploadOpen, setFolderUploadOpen] = useState(false);
  const [libraryPickerOpen, setLibraryPickerOpen] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);
  const invalidateLibrary = useInvalidateLibrary();
  const { selectedAuditYearId } = useAuditYear();
  const [saving, setSaving]             = useState(false);
  const [scopeOpen, setScopeOpen]       = useState(false);

  // AI mapping state
  const [aiSugg, setAiSugg]           = useState({}); // { [controlUploadId]: { attrs: {[ref]:{conf,excerpt}}, samples: {[ref]:conf} } }
  const [aiRunning, setAiRunning]      = useState(false);
  const [aiDone, setAiDone]            = useState(false);
  const [aiError, setAiError]          = useState('');

  const fileRef     = useRef(null);
  const debounceRef = useRef({});
  const syncedRef   = useRef(new Set());

  // Plan attributes loaded from the run's plan snapshot (latest saved test plan)
  const [planAttributes, setPlanAttributes] = useState(null); // null = loading

  const periodStart = run?.testing_period_start ? String(run.testing_period_start).split('T')[0] : '';
  const periodEnd   = run?.testing_period_end   ? String(run.testing_period_end).split('T')[0]   : '';

  // Use plan snapshot attributes when available; fall back to control.test_steps
  const attributes  = planAttributes ?? buildAttributeList(control?.test_steps);
  const allAttrRefs = attributes.map(a => a.ref);
  // Runs without a population test from evidence files: the auditor names the samples
  // here and tags several files to each one. Otherwise samples come from the population.
  const notRequired = isPopulationNotRequired(run);
  const [localSamples, setLocalSamples]     = useState(null);
  const [sampleSaving, setSampleSaving]     = useState(false);
  const [newSampleLabel, setNewSampleLabel] = useState('');
  const samples     = (localSamples ?? run?.population_data?.selected_samples ?? []).map(String);
  // SAMPLE-1, SAMPLE-2, … labels — use stored map if available (set at selection time), fall back to index.
  // Evidence-file samples keep the label the auditor typed.
  const storedLabels = run?.population_data?.sample_labels || {};
  const sampleLabels = {};
  samples.forEach((s, i) => { sampleLabels[s] = notRequired ? s : (storedLabels[s] || `SAMPLE-${i + 1}`); });

  /* ── Parsing ── */
  const parseMapping = (m) => {
    const empty = { steps: [], samples: [] };
    if (!m) return empty;
    const tryJson = (s) => { try { return JSON.parse(s); } catch { return undefined; } };
    let v = m;
    if (typeof v === 'string') { let p = tryJson(v); if (p === undefined) { try { p = tryJson(atob(v)); } catch { } } v = p; if (typeof v === 'string') v = tryJson(v); }
    if (Array.isArray(v)) return { steps: v.map(String), samples: [] };
    return { steps: Array.isArray(v?.steps) ? v.steps.map(String) : [], samples: Array.isArray(v?.samples) ? v.samples.map(String) : [] };
  };

  const detectSampleFromFilename = (filename) => {
    if (!samples.length) return [];
    const clean = (s) => s.replace(/[-\s_.]/g, '').toLowerCase();
    const cleanedFilename = clean(filename);
    return samples.filter(s => { const cs = clean(s); return cs.length >= 4 && cleanedFilename.includes(cs); });
  };

  /* ── Fetch evidence ── */
  const fetchEvidence = useCallback(async () => {
    if (!controlUuid) return;
    setLoading(true);
    try {
      const resp = await axiosInstance.get(`/api/sox-controls/${controlUuid}/evidence`);
      const raw = (Array.isArray(resp.data) ? resp.data : []).map(e => {
        const m = parseMapping(e.step_mapping);
        return { ...e, _attributes: m.steps, _samples: m.samples };
      });
      // Collapse true re-uploads only (same category, name AND size); different documents
      // that share a name are kept and tagged v1/v2… so they can be told apart.
      const list = labelSameNameVersions(dedupeEvidenceFiles(raw));
      setAllFiles(list);
      const runId = run?.executionId || run?.id;
      if (runId) {
        try {
          const evResp = await axiosInstance.get(`/api/control-test-runs/${runId}/evidence`);
          const linked = Array.isArray(evResp.data) ? evResp.data : [];
          const linkedUploadIds = new Set(linked.map(e => e.file_id).filter(Boolean));
          const preSelected = new Set(
            list.filter(f => linkedUploadIds.has(f.control_upload_id) && (f.file_category || 'evidence') === 'evidence').map(f => f.id)
          );
          setSelectedIds(preSelected);
        } catch { setSelectedIds(new Set()); }
      }
    } catch { setAllFiles([]); } finally { setLoading(false); }
  }, [controlUuid, run?.id, run?.executionId]); // eslint-disable-line

  useEffect(() => { fetchEvidence(); }, [fetchEvidence]);

  // Load attributes from the run's plan snapshot (latest saved test plan for this run)
  useEffect(() => {
    const runId = run?.executionId || run?.id;
    if (!runId) return;
    axiosInstance.get(`/api/control-test-runs/${runId}/plan`)
      .then(resp => {
        const attrs = (resp.data?.attributes || []).map(a => ({
          id:          a.id,
          ref:         a.display_ref || a.ref || '',
          label:       a.label || a.display_ref || a.id,
          description: a.description || '',
          steps:       a.steps || a.procedure_steps || [],
        })).filter(a => a.ref);
        if (attrs.length > 0) setPlanAttributes(attrs);
      })
      .catch(() => { /* fall back to control.test_steps */ });
  }, [run?.executionId, run?.id]); // eslint-disable-line

  /* ── Auto-save debounce ── */
  useEffect(() => {
    allFiles.forEach(ev => {
      if (!ev._dirty) return;
      clearTimeout(debounceRef.current[ev.id]);
      debounceRef.current[ev.id] = setTimeout(() => saveMapping(ev), 800);
    });
    return () => {
      Object.values(debounceRef.current).forEach(clearTimeout);
    };
  }, [allFiles]); // eslint-disable-line

  /* ── Upload ── */
  const handleFileInputChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (fileRef.current) fileRef.current.value = '';
    if (files.length === 1) setPickerFile({ files });
    else await doUpload(files, 'evidence');
  };

  const runId = run?.executionId || run?.id;

  // Latest list and selection, for the async handlers below (their closures are from an older render).
  const allFilesRef = useRef(allFiles);
  allFilesRef.current = allFiles;
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  // Add evidence files to this test. Files that share a name are fine: the test engine tells them apart by file id
  // (each gets a run-unique label, e.g. "INV1043 - approval.pdf").
  const addToTest = (ids, list) => {
    const next = new Set(selectedIdsRef.current);
    ids.forEach(id => {
      const candidate = list.find(e => e.id === id);
      if (candidate && (candidate.file_category || 'evidence') === 'evidence') next.add(id);
    });
    selectedIdsRef.current = next;
    setSelectedIds(next);
  };

  // Reload the control's evidence after files were added (upload, folder upload or library pick) and add them to
  // this test. Rows already on screen keep their local mapping. Files with no mapping yet get the default (every
  // attribute; the samples their name or folder points to, or `defaultSamples`), saved a few at a time.
  const absorbNewEvidence = async (selectEvidenceIds = new Set(), defaultSamples = {}) => {
    const resp = await axiosInstance.get(`/api/sox-controls/${controlUuid}/evidence`);
    const raw = (Array.isArray(resp.data) ? resp.data : []).map(e => {
      const m = parseMapping(e.step_mapping);
      return { ...e, _attributes: m.steps, _samples: m.samples };
    });
    const fresh = labelSameNameVersions(dedupeEvidenceFiles(raw));
    const prevById = new Map(allFilesRef.current.map(f => [f.id, f]));
    const toSave = [];
    const merged = fresh.map(e => {
      const prev = prevById.get(e.id);
      const isEvidence = (e.file_category || 'evidence') === 'evidence';
      const local = prev ? { ...e, _attributes: prev._attributes, _samples: prev._samples, _dirty: prev._dirty } : e;
      const wanted = !prev || selectEvidenceIds.has(e.id);
      if (!wanted || !isEvidence || local._attributes.length > 0 || local._samples.length > 0) return local;
      const suggested = defaultSamples[e.id] || [];
      const withDefaults = {
        ...local,
        _attributes: [...allAttrRefs],
        _samples: suggested.length ? suggested : detectSampleFromFilename(e.filename || ''),
        _dirty: false,
      };
      toSave.push(withDefaults);
      return withDefaults;
    });
    allFilesRef.current = merged;
    setAllFiles(merged);
    const newIds = fresh.filter(e => !prevById.has(e.id)).map(e => e.id);
    addToTest([...newIds, ...selectEvidenceIds], merged);
    // Reset AI state since library changed
    setAiDone(false);
    // A few at a time: a 300-file folder must not fire 600 requests at once. A row the user edited before its turn
    // is skipped here; their edit is saved by the auto-save instead of being overwritten with the default.
    runPool(toSave, 4, async (planned) => {
      const current = allFilesRef.current.find(e => e.id === planned.id);
      if (!current || current._dirty || current._attributes !== planned._attributes || current._samples !== planned._samples) return;
      await saveMapping(current);
    });
  };

  const handleFolderUploaded = async (result) => {
    const ids = new Set(Object.values(result.outcomes).map(o => o.evidenceId).filter(Boolean));
    try { await absorbNewEvidence(ids); } catch { /* the list reloads when the modal reopens */ }
  };

  const handleLibraryPicks = async (picks, controlPicks = []) => {
    const selectIds = new Set(controlPicks.map(p => p.evidenceId));
    const defaultSamples = Object.fromEntries(controlPicks.map(p => [p.evidenceId, p.samples]));
    if (!picks.length) {
      await absorbNewEvidence(selectIds, defaultSamples);
      setLibraryPickerOpen(false);
      return;
    }
    const results = await linkLibraryFiles({
      sox_control_id: controlUuid,
      test_run_id: runId || undefined,
      items: picks.map(p => ({
        control_upload_id: p.file.id,
        file_category: p.role,
        testing_period_start: periodStart || undefined,
        testing_period_end: periodEnd || undefined,
        step_mapping: p.role === 'evidence' ? { steps: [...allAttrRefs], samples: p.samples } : undefined,
      })),
    });
    const failed = results.filter(r => r.status === 'error');
    results.filter(r => r.status !== 'error' && r.evidence_id).forEach(r => selectIds.add(r.evidence_id));
    await absorbNewEvidence(selectIds, defaultSamples);
    invalidateLibrary();
    if (failed.length) {
      toast.error(`${failed.length} file${failed.length > 1 ? 's' : ''} could not be added: ${failed[0].error}`);
    }
    const already = results.filter(r => r.status === 'already_linked' && r.file_category && r.file_category !== picks.find(p => p.file.id === r.control_upload_id)?.role);
    if (already.length) {
      toast(`${already.length} file${already.length > 1 ? 's were' : ' was'} already on this control with another role; the existing role was kept.`, { icon: 'ℹ️' });
    }
    const otherRoles = picks.filter(p => p.role !== 'evidence' && results.some(r => r.control_upload_id === p.file.id && r.status !== 'error'));
    if (otherRoles.length) {
      toast.success(
        `${otherRoles.length} file${otherRoles.length !== 1 ? 's' : ''} added to the control as population, IPE or supporting documents. They are not mapped in this test.`,
        { duration: 6000 },
      );
    }
    setLibraryPickerOpen(false);
  };

  const doUpload = async (files, category) => {
    setPickerFile(null);
    setUploading(true);
    try {
      // Skip only true re-uploads (same name, size and role); a different document that shares a name is uploaded.
      const toUpload = files.filter(f =>
        !allFiles.some(e => e.filename === f.name && e.file_size === f.size && (e.file_category || 'evidence') === category)
      );
      if (toUpload.length < files.length) {
        const skipped = files.length - toUpload.length;
        const categoryLabel = CATEGORIES[category]?.label || category;
        if (toUpload.length === 0) {
          // Give a specific message — name the category so users know where to look
          toast(
            skipped === 1
              ? `This file is already uploaded as "${categoryLabel}" for this control.`
              : `${skipped} files are already uploaded as "${categoryLabel}" for this control.`,
            { icon: 'ℹ️' }
          );
          setUploading(false);
          return;
        }
        toast(`${skipped} file${skipped > 1 ? 's' : ''} already exist as "${categoryLabel}" and will be skipped.`, { icon: 'ℹ️' });
      }
      const responses = await Promise.all(toUpload.map(f => {
        const form = new FormData();
        form.append('file', f);
        form.append('evidence_type', 'pbc');
        form.append('file_category', category);
        if (periodStart) form.append('testing_period_start', periodStart);
        if (periodEnd)   form.append('testing_period_end',   periodEnd);
        if (runId)       form.append('test_run_id', runId);
        return axiosInstance.post(`/api/sox-controls/${controlUuid}/evidence/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      }));
      // The server may answer with a file the control already had (same content): add those to the test as well.
      const uploadedIds = new Set(responses.map(r => r?.data?.id).filter(Boolean));
      await absorbNewEvidence(uploadedIds);
      invalidateLibrary();
      if (category !== 'evidence') {
        toast.success(
          `${toUpload.length} file${toUpload.length !== 1 ? 's' : ''} added to the control as ${CATEGORIES[category]?.label || category}. ` +
          'Population and IPE files are used in the test setup; supporting files are kept with the control, not tested.',
          { duration: 6000 },
        );
      }
    } catch (err) {
      toast.error('Upload failed: ' + resolve403ErrorMessage(err, err.response?.data?.error || err.message));
    } finally { setUploading(false); }
  };

  /* ── AI mapping ── */
  const runAIMapping = async () => {
    const evidenceFiles = allFiles.filter(e => selectedIds.has(e.id) && (e.file_category || 'evidence') === 'evidence');
    if (!evidenceFiles.length) { toast('Add at least one evidence file to this test first.', { icon: '⚠️' }); return; }
    if (!samples.length) { toast('No samples found on this run — complete population setup first.', { icon: '⚠️' }); return; }

    setAiRunning(true);
    setAiError('');
    setAiDone(false);
    try {
      const cells = await aiMapEvidence({
        controlUuid,
        controlTitle: control?.title || control?.control_title || '',
        controlObjective: control?.objective || control?.control_objective || '',
        samples,
        attributes: attributes.map(a => ({
          id: a.id || a.ref,
          ref: a.ref,
          label: a.label,
          description: a.description,
          steps: (a.steps || []).map(s => ({ step_number: s.step_number ?? s.stepNumber, instruction: s.instruction || s.description || '' })).filter(s => s.instruction),
        })),
        fields: (planAttributes || []).flatMap(a => a.steps || []).map(s => ({
          field_key: s.field_key || s.key || '',
          label: s.label || s.instruction || '',
        })).filter(f => f.field_key),
        evidenceFiles,
      });

      // Transform per-(sample × attribute × file) cells → per-file suggestions
      const sugg = {};
      cells.forEach(cell => {
        const fid = String(cell.file_id ?? '');
        if (!fid) return;
        if (!sugg[fid]) sugg[fid] = { attrs: {}, samples: {} };
        const attrRef = cell.attribute_ref || cell.attribute_id || '';
        const sampleRef = String(cell.sample_ref || cell.record_ref || '');
        const conf = Math.round(Number(cell.confidence) || 0);
        const excerpt = cell.excerpt || '';
        if (attrRef) {
          if (!sugg[fid].attrs[attrRef] || sugg[fid].attrs[attrRef].conf < conf)
            sugg[fid].attrs[attrRef] = { conf, excerpt };
        }
        if (sampleRef) {
          if (!sugg[fid].samples[sampleRef] || sugg[fid].samples[sampleRef] < conf)
            sugg[fid].samples[sampleRef] = conf;
        }
      });
      setAiSugg(sugg);
      setAiDone(true);
    } catch (err) {
      setAiError(resolve403ErrorMessage(err, err.response?.data?.error || err.message || 'AI mapping failed'));
    } finally { setAiRunning(false); }
  };

  // Accept AI suggestions for a single file (merge into confirmed _attributes/_samples)
  const acceptFileSugg = (ev) => {
    const fid = String(ev.control_upload_id ?? ev.id ?? '');
    const sugg = aiSugg[fid];
    if (!sugg) return;
    const newAttrs = Array.from(new Set([...ev._attributes, ...Object.keys(sugg.attrs)]));
    // Pick sample with highest confidence if none already set
    let newSamples = [...ev._samples];
    if (!newSamples.length) {
      const topSample = Object.entries(sugg.samples).sort((a, b) => b[1] - a[1])[0]?.[0];
      if (topSample) newSamples = [topSample];
    }
    setAllFiles(prev => prev.map(e => e.id === ev.id ? { ...e, _attributes: newAttrs, _samples: newSamples, _dirty: true } : e));
    // Remove this file from pending suggestions
    setAiSugg(prev => { const next = { ...prev }; delete next[fid]; return next; });
  };

  const acceptAllSugg = () => {
    setAllFiles(prev => prev.map(ev => {
      const fid = String(ev.control_upload_id ?? ev.id ?? '');
      const sugg = aiSugg[fid];
      if (!sugg) return ev;
      const newAttrs = Array.from(new Set([...ev._attributes, ...Object.keys(sugg.attrs)]));
      let newSamples = [...ev._samples];
      if (!newSamples.length) {
        const topSample = Object.entries(sugg.samples).sort((a, b) => b[1] - a[1])[0]?.[0];
        if (topSample) newSamples = [topSample];
      }
      return { ...ev, _attributes: newAttrs, _samples: newSamples, _dirty: true };
    }));
    setAiSugg({});
  };

  const hasPendingSugg = Object.keys(aiSugg).length > 0;
  const pendingSuggCount = allFiles.filter(e => aiSugg[String(e.control_upload_id ?? e.id ?? '')]).length;

  /* ── Selection ── */
  const toggleSelect = (id) => {
    setSelectedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
    setAiDone(false); // library change invalidates current AI mapping
  };

  /* ── Mapping helpers ── */
  const setDirty = (evId, patch) =>
    setAllFiles(prev => prev.map(e => e.id === evId ? { ...e, ...patch, _dirty: true } : e));

  /* ── Evidence-file samples (runs without a population) ── */
  const saveSampleList = async (next) => {
    const runId = run?.executionId || run?.id;
    if (!runId) return false;
    setSampleSaving(true);
    try {
      await syncEvidenceSamples(runId, next);
      setLocalSamples(next);
      return true;
    } catch (err) {
      toast.error('Failed to save samples: ' + resolve403ErrorMessage(err, err.response?.data?.error || err.message));
      return false;
    } finally { setSampleSaving(false); }
  };

  const addSample = async (rawLabel) => {
    const label = String(rawLabel || '').replace(/\s+/g, ' ').trim();
    if (!label) return;
    if (label.length > 255) { toast.error('Sample name is too long (max 255 characters).'); return; }
    // Evidence is matched on the normalized label, so "Invoice A" and "invoice a" are one sample.
    const clash = findLabelCollision(samples, label);
    if (clash) {
      toast.error(clash === label
        ? 'A sample with that name already exists.'
        : `"${label}" is the same sample as "${clash}" (names are matched ignoring case and spacing).`);
      return;
    }
    if (await saveSampleList([...samples, label])) setNewSampleLabel('');
  };

  const removeSample = async (label) => {
    if (!(await saveSampleList(samples.filter(s => s !== label)))) return;
    // Untag the files that pointed at it; the debounced auto-save persists the change.
    setAllFiles(prev => prev.map(e => e._samples.includes(label)
      ? { ...e, _samples: e._samples.filter(x => x !== label), _dirty: true } : e));
  };

  // One sample per unassigned evidence file, named after the file.
  const samplePerFile = async () => {
    const untagged = allFiles.filter(e =>
      selectedIds.has(e.id) && (e.file_category || 'evidence') === 'evidence' && e._samples.length === 0);
    if (untagged.length === 0) return;
    const next = [...samples];
    const tagByFile = {};
    untagged.forEach(ev => {
      const base = String(ev.filename || `File ${ev.id}`).replace(/\.[^.]+$/, '').slice(0, 240);
      const label = uniqueSampleLabel(base, next);
      next.push(label);
      tagByFile[ev.id] = label;
    });
    if (!(await saveSampleList(next))) return;
    setAllFiles(prev => prev.map(e => tagByFile[e.id]
      ? { ...e, _samples: [...e._samples, tagByFile[e.id]], _dirty: true } : e));
  };

  const toggleAttribute = (evId, ref) => {
    const ev = allFiles.find(e => e.id === evId);
    if (!ev) return;
    const fid = String(ev.control_upload_id ?? ev.id ?? '');
    const sugg = aiSugg[fid];
    // If it's an AI suggestion being clicked → accept just this attr + remove from sugg
    if (sugg?.attrs[ref] && !ev._attributes.includes(ref)) {
      const newAttrs = [...ev._attributes, ref];
      const newSugg = { ...sugg.attrs };
      delete newSugg[ref];
      const remainingSugg = { ...sugg, attrs: newSugg };
      setAllFiles(prev => prev.map(e => e.id === evId ? { ...e, _attributes: newAttrs, _dirty: true } : e));
      setAiSugg(prev => Object.keys(remainingSugg.attrs).length === 0 && !Object.keys(remainingSugg.samples).length
        ? (() => { const n = { ...prev }; delete n[fid]; return n; })()
        : { ...prev, [fid]: remainingSugg });
    } else {
      setDirty(evId, {
        _attributes: ev._attributes.includes(ref)
          ? ev._attributes.filter(r => r !== ref)
          : [...ev._attributes, ref],
      });
    }
  };

  const toggleSample = (evId, sample) => {
    const ev = allFiles.find(e => e.id === evId);
    if (!ev) return;
    const fid = String(ev.control_upload_id ?? ev.id ?? '');
    const sugg = aiSugg[fid];
    if (sugg?.samples[sample] && !ev._samples.includes(sample)) {
      const newSamples = [...ev._samples, sample];
      const newSampSugg = { ...sugg.samples }; delete newSampSugg[sample];
      const remainingSugg = { ...sugg, samples: newSampSugg };
      setAllFiles(prev => prev.map(e => e.id === evId ? { ...e, _samples: newSamples, _dirty: true } : e));
      setAiSugg(prev => Object.keys(remainingSugg.attrs).length === 0 && Object.keys(remainingSugg.samples).length === 0
        ? (() => { const n = { ...prev }; delete n[fid]; return n; })()
        : { ...prev, [fid]: remainingSugg });
    } else {
      setDirty(evId, {
        _samples: ev._samples.includes(sample)
          ? ev._samples.filter(s => s !== sample)
          : [...ev._samples, sample],
      });
    }
  };

  // eslint-disable-next-line no-unused-vars
  const setAllAttributes = (evId, on) => setDirty(evId, { _attributes: on ? [...allAttrRefs] : [] });
  const setAllSamples    = (evId, on) => setDirty(evId, { _samples: on ? [...samples] : [] });

  /* ── Category change ── */
  const changeCategory = async (ev, newCategory) => {
    try {
      await axiosInstance.patch(`/api/sox-controls/${controlUuid}/evidence/${ev.id}/category`, { file_category: newCategory });
      setAllFiles(prev => prev.map(e => e.id === ev.id ? { ...e, file_category: newCategory } : e));
    } catch (err) { toast.error('Failed to update category: ' + resolve403ErrorMessage(err, err.response?.data?.error || err.message)); }
  };

  /* ── Sync + save ── */
  const syncEvidenceRecord = useCallback(async (ev) => {
    const runId = run?.executionId || run?.id;
    if (!runId || !ev._samples?.length) return;
    await Promise.allSettled(ev._samples.map(recordRef => {
      const key = `${runId}:${ev.id}:${recordRef}`;
      if (syncedRef.current.has(key)) return Promise.resolve();
      syncedRef.current.add(key);
      return axiosInstance.post(`/api/control-test-runs/${runId}/evidence`, {
        record_ref: recordRef,
        file_id:    ev.control_upload_id ?? null,
        file_uri:   ev.file_ref ?? null,
        file_name:  ev.filename ?? '',
        file_type:  ev.content_type ?? ev.file_type ?? '',
      }).catch((syncErr) => {
        syncedRef.current.delete(key);
        if (syncErr?.response?.status === 403) {
          toast.error(
            'Failed to sync evidence: ' +
              resolve403ErrorMessage(syncErr, syncErr.response?.data?.error || syncErr.message)
          );
        }
      });
    }));
  }, [run]);

  const saveMapping = async (ev) => {
    setSavingIds(prev => new Set(prev).add(ev.id));
    try {
      await axiosInstance.put(`/api/sox-controls/${controlUuid}/evidence/${ev.id}/mapping`, {
        step_mapping: { steps: ev._attributes, samples: ev._samples },
      });
      setAllFiles(prev => prev.map(e => e.id === ev.id ? { ...e, _dirty: false } : e));
      setSavedIds(prev => { const s = new Set(prev); s.add(ev.id); return s; });
      setTimeout(() => setSavedIds(prev => { const s = new Set(prev); s.delete(ev.id); return s; }), 2000);
      await syncEvidenceRecord(ev);
    } catch (err) {
      toast.error('Failed to save mapping: ' + resolve403ErrorMessage(err, err.response?.data?.error || err.message));
    } finally { setSavingIds(prev => { const s = new Set(prev); s.delete(ev.id); return s; }); }
  };

  const handleSaveAndRun = async () => {
    Object.values(debounceRef.current).forEach(t => clearTimeout(t));
    debounceRef.current = {};
    const selectedFiles = allFiles.filter(e => selectedIds.has(e.id));
    const dirty = selectedFiles.filter(e => e._dirty);
    if (dirty.length > 0) await Promise.allSettled(dirty.map(ev => saveMapping(ev)));
    await Promise.allSettled(selectedFiles.map(ev => syncEvidenceRecord(ev)));

    // Build confirmed (sample × attribute × file) cells from the explicit mapping on each file
    const runId = run?.executionId || run?.id;
    const evidenceFiles = selectedFiles.filter(e => (e.file_category || 'evidence') === 'evidence');
    if (runId && evidenceFiles.length > 0) {
      setSaving(true);
      try {
        const cells = [];
        evidenceFiles.forEach(ev => {
          const fileId = ev.control_upload_id ?? null;
          ev._samples.forEach(sampleRef => {
            ev._attributes.forEach(attrRef => {
              const attr = attributes.find(a => a.ref === attrRef);
              cells.push({
                record_ref:    sampleRef,
                sample_ref:    sampleRef,
                attribute_id:  attr?.id || attrRef,
                attribute_ref: attrRef,
                evidence_id:   fileId,
                file_id:       fileId,
                file_name:     ev.filename || '',
                confidence:    100,
                source:        'manual',
                review_status: 'confirmed',
              });
            });
          });
        });
        if (cells.length > 0) {
          await saveEvidenceMap(runId, cells);
        }
      } catch (saveErr) {
        if (saveErr?.response?.status === 403) {
          toast.error(
            'Failed to save evidence map: ' +
              resolve403ErrorMessage(saveErr, saveErr.response?.data?.error || saveErr.message)
          );
        }
      } finally { setSaving(false); }
    }

    onSaved?.({ analysisMode });
    onClose();
  };

  /* ── Derived ── */
  const selectedFiles  = allFiles.filter(e => selectedIds.has(e.id));
  // Evidence files on this control not yet in this test — offered first in "Add from library".
  const libraryFiles   = allFiles.filter(e => !selectedIds.has(e.id) && (e.file_category || 'evidence') === 'evidence');
  const selectedEvidenceFiles = selectedFiles.filter(e => (e.file_category || 'evidence') === 'evidence');
  // eslint-disable-next-line no-unused-vars
  const selectedByCategory = CATEGORY_ORDER.reduce((acc, cat) => {
    acc[cat] = selectedFiles.filter(e => (e.file_category || 'evidence') === cat);
    return acc;
  }, {});

  return (
    <>
      {folderUploadOpen && (
        <FolderUploadDialog
          title="Upload evidence folder"
          context={{
            scope: runId ? 'run' : 'control',
            soxControlId: controlUuid,
            testRunId: runId || undefined,
            fileCategory: 'evidence',
            periodStart: periodStart || undefined,
            periodEnd: periodEnd || undefined,
          }}
          onClose={() => setFolderUploadOpen(false)}
          onUploaded={handleFolderUploaded}
        />
      )}
      {libraryPickerOpen && (
        <LibraryPicker
          soxControlId={controlUuid}
          testRunId={runId || undefined}
          auditYearId={run?.audit_year_id || selectedAuditYearId || undefined}
          roles={['evidence', 'population', 'ipe_proof', 'supporting']}
          samples={samples.map(s => ({ id: s, label: sampleLabels[s] || s }))}
          title="Add evidence to this test"
          linkedUploadIds={new Set(allFiles.map(f => f.control_upload_id))}
          inTestUploadIds={new Set(selectedFiles.map(f => f.control_upload_id))}
          controlFiles={libraryFiles.map(f => ({
            evidenceId: f.id,
            uploadId: f.control_upload_id,
            filename: f.filename,
            path: f.relative_path,
            role: f.file_category || 'evidence',
            note: f._versionLabel ? `${f._versionLabel}${f._versionTitle ? ` · ${f._versionTitle.split(' · ').slice(1).join(' · ')}` : ''}` : undefined,
          }))}
          onConfirm={handleLibraryPicks}
          onClose={() => setLibraryPickerOpen(false)}
        />
      )}
      {pickerFile && (
        <CategoryPickerModal
          filename={pickerFile.files[0]?.name || ''}
          onConfirm={(cat) => doUpload(pickerFile.files, cat)}
          onCancel={() => setPickerFile(null)}
        />
      )}

      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Upload and Map Your Evidence</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {run?.test_run_name && run.test_run_name !== 'Test run'
                  ? run.test_run_name
                  : run?.test_run_number ? `RUN-${run.test_run_number}` : 'New Test Run'}
                {periodStart && periodEnd ? ` · Period: ${periodStart} — ${periodEnd}` : ''}
              </p>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="h-5 w-5 text-gray-500" /></button>
          </div>

          {/* AI banner — shown when AI has run */}
          {aiDone && hasPendingSugg && (
            <div className="flex items-center gap-3 px-6 py-2.5 bg-violet-50 border-b border-violet-200 shrink-0">
              <Sparkles className="h-4 w-4 text-violet-600 shrink-0" />
              <p className="text-xs text-violet-800 flex-1">
                <strong>AI mapped {pendingSuggCount} file{pendingSuggCount !== 1 ? 's' : ''}</strong> — suggestions shown as violet chips with confidence %. Hover any attribute chip to see testing steps. Click a chip to accept it, or use Accept All.
              </p>
              <div className="flex items-center gap-1.5 text-[10px] text-violet-600 shrink-0">
                <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>≥85%
                <span className="inline-block w-2 h-2 rounded-full bg-amber-500 ml-1"></span>60–84%
                <span className="inline-block w-2 h-2 rounded-full bg-red-500 ml-1"></span>&lt;60%
              </div>
              <button onClick={acceptAllSugg}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-violet-600 hover:bg-violet-700 px-3 py-1.5 rounded-lg shrink-0">
                <Check className="h-3.5 w-3.5" /> Accept All
              </button>
            </div>
          )}
          {aiError && (
            <div className="px-6 py-2 bg-red-50 border-b border-red-200 text-xs text-red-700 shrink-0">
              ⚠ AI mapping failed: {aiError}
            </div>
          )}

          {/* Two-panel body */}
          <div className="flex flex-1 overflow-hidden min-h-0">

            {/* LEFT: This Test */}
            <div className="w-[58%] flex flex-col border-r border-gray-200">
              <div className="flex items-center flex-wrap gap-2 px-5 py-2.5 bg-slate-50 border-b border-gray-200 shrink-0">
                <FlaskConical className="h-4 w-4 text-slate-500" />
                <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">This Test</span>
                <span className="text-[10px] bg-slate-200 text-slate-600 rounded-full px-2 py-0.5">{selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''}</span>
                {hasPendingSugg && <span className="text-[10px] bg-violet-100 text-violet-700 rounded-full px-2 py-0.5">✦ {pendingSuggCount} AI-mapped</span>}
                <div className="flex-1" />
                {/* AI Map button */}
                <button
                  onClick={runAIMapping}
                  disabled={aiRunning || !selectedEvidenceFiles.length}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50 px-3 py-1.5 rounded-lg"
                >
                  {aiRunning ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Mapping…</> : <><Sparkles className="h-3.5 w-3.5" /> {aiDone ? 'Re-run AI Map' : 'AI Map Evidence'}</>}
                </button>
                <input ref={fileRef} type="file" multiple className="hidden" onChange={handleFileInputChange} />
                <button onClick={() => setLibraryPickerOpen(true)} disabled={uploading || !controlUuid}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 px-3 py-1.5 rounded-lg">
                  <Library className="h-3.5 w-3.5" /> Add from library
                  {libraryFiles.length > 0 && <span className="rounded-full bg-white/20 px-1.5 text-[10px]">{libraryFiles.length}</span>}
                </button>
                <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 disabled:opacity-50 px-3 py-1.5 rounded-lg">
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  {uploading ? 'Uploading…' : 'Upload files'}
                </button>
                <button onClick={() => setFolderUploadOpen(true)} disabled={uploading || !controlUuid}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 disabled:opacity-50 px-3 py-1.5 rounded-lg">
                  <FolderUp className="h-3.5 w-3.5" /> Upload folder
                </button>
                <div className="relative">
                  <button type="button" onClick={() => setLegendOpen(o => !o)} aria-expanded={legendOpen}
                    aria-label="What the attribute chip colours mean" title="What the chip colours mean"
                    className="p-1 text-gray-400 hover:text-gray-600 rounded">
                    <Info className="h-4 w-4" />
                  </button>
                  {legendOpen && (
                    <div className="absolute right-0 top-7 z-30 w-64 rounded-lg border border-gray-200 bg-white p-3 shadow-lg space-y-1.5">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Attribute chips</p>
                      <div className="flex items-center gap-2 text-[11px] text-gray-600">
                        <span className="w-3 h-3 rounded-full bg-indigo-200 shrink-0" /> Confirmed (manual or AI accepted)
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-gray-600">
                        <span className="w-3 h-3 rounded-full bg-violet-400 shrink-0" /> AI suggested — click to accept
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-gray-600">
                        <span className="w-3 h-3 rounded-full bg-gray-200 shrink-0" /> Not mapped — click to add
                      </div>
                      <p className="text-[10px] text-gray-400 italic">Hover a chip to see its testing steps.</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-auto min-h-0">
                {loading && <div className="flex items-center justify-center py-10 text-gray-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>}

                {!loading && selectedFiles.length === 0 && (
                  <div className="text-center py-12">
                    <FolderOpen className="h-8 w-8 text-gray-200 mx-auto mb-3" />
                    <p className="text-sm text-gray-400">No files selected for this test yet.</p>
                    <p className="text-xs text-gray-400 mt-1">Use <b>Add from library</b> for files already provided, or upload new ones above.</p>
                  </div>
                )}

                {!loading && selectedFiles.length > 0 && (
                  <table className="w-full text-xs border-collapse">
                    <thead className="sticky top-0 z-10 bg-slate-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">File</th>
                        <th className="px-2 py-2 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">Samples</th>
                        {attributes.map(attr => (
                          <th key={attr.ref} className="px-1 py-2 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wide w-12 whitespace-nowrap" title={attr.label || attr.ref}>
                            {attr.ref}
                          </th>
                        ))}
                        <th className="w-8" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {selectedFiles.map(ev => (
                        <SelectedFileRow
                          key={ev.id}
                          ev={ev}
                          sampleLabels={sampleLabels}
                          samples={samples}
                          attributes={attributes}
                          savingIds={savingIds}
                          savedIds={savedIds}
                          aiSugg={aiSugg}
                          onAcceptFileSugg={acceptFileSugg}
                          onToggleSample={toggleSample}
                          onSetAllSamples={setAllSamples}
                          onToggleSelect={toggleSelect}
                          onToggleAttribute={toggleAttribute}
                        />
                      ))}
                    </tbody>
                  </table>
                )}
              </div>


              {/* Scope summary */}
              <ScopePanel
                selectedEvidenceFiles={selectedEvidenceFiles}
                sampleLabels={sampleLabels}
                scopeOpen={scopeOpen}
                setScopeOpen={setScopeOpen}
              />
            </div>

            {/* RIGHT: Samples */}
            <div className="w-[42%] flex flex-col bg-gray-50">
              <div className="flex items-center gap-2 px-5 py-2.5 border-b border-gray-200 shrink-0">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Samples</span>
                <span className="text-[10px] bg-gray-200 text-gray-600 rounded-full px-2 py-0.5">{samples.length} {notRequired ? `sample${samples.length !== 1 ? 's' : ''}` : 'selected'}</span>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2 min-h-0">

                {/* No population: the auditor creates samples from evidence files */}
                {notRequired && (
                  <div className="border border-dashed border-indigo-300 rounded-lg p-3 bg-indigo-50/40 space-y-2">
                    <p className="text-[11px] text-indigo-800">
                      No population for this test. Each sample is a group of evidence files — name a sample,
                      then tag one or more files to it from the <strong>Samples</strong> column.
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newSampleLabel}
                        onChange={e => setNewSampleLabel(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void addSample(newSampleLabel); } }}
                        placeholder="e.g. Invoice 1042"
                        maxLength={255}
                        className="flex-1 min-w-0 px-2 py-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-indigo-400"
                      />
                      <button
                        type="button"
                        onClick={() => addSample(newSampleLabel)}
                        disabled={sampleSaving || !newSampleLabel.trim()}
                        className="inline-flex items-center gap-1 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 px-3 py-1.5 rounded-lg"
                      >
                        {sampleSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Add sample
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={samplePerFile}
                      disabled={sampleSaving || selectedEvidenceFiles.every(e => e._samples.length > 0)}
                      className="text-[11px] text-indigo-700 hover:underline disabled:text-gray-400 disabled:no-underline"
                    >
                      Create one sample per unassigned file
                    </button>
                  </div>
                )}

                {/* Sample list with sequential numbering */}
                {samples.length === 0 && (
                  <p className="text-xs text-gray-400 italic text-center pt-6">
                    {notRequired ? 'No samples yet — add one above.' : 'No samples selected in this test run.'}
                  </p>
                )}
                {samples.map((s, i) => {
                  const mappedFiles = selectedEvidenceFiles.filter(ev => ev._samples.includes(s));
                  return (
                    <div key={s} className="border border-gray-200 rounded-lg p-3 bg-white">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-gray-700 truncate" title={s}>{notRequired ? s : `SAMPLE-${i + 1}`}</p>
                        <div className="flex items-center gap-2 shrink-0">
                          {mappedFiles.length > 0
                            ? <span className="text-[10px] text-green-600 font-semibold">{mappedFiles.length} file{mappedFiles.length !== 1 ? 's' : ''} ✓</span>
                            : <span className="text-[10px] text-amber-500">No files mapped</span>}
                          {notRequired && (
                            <button type="button" onClick={() => removeSample(s)} disabled={sampleSaving}
                              title="Remove sample" className="p-0.5 text-gray-300 hover:text-red-400 disabled:opacity-50">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      {!notRequired && <p className="text-[10px] text-gray-400 truncate mt-0.5" title={s}>{s}</p>}
                      {mappedFiles.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {mappedFiles.map(ev => (
                            <div key={ev.id} className="flex items-center gap-1.5 text-[11px] text-gray-600">
                              <Check className="h-3 w-3 text-green-500 shrink-0" />
                              <span className="truncate" title={ev.filename}>{ev.filename}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between shrink-0">
            <div className="text-xs text-gray-500">
              {selectedEvidenceFiles.filter(fileIsMapped).length > 0
                ? `${selectedEvidenceFiles.filter(fileIsMapped).length} of ${selectedEvidenceFiles.length} evidence file${selectedEvidenceFiles.length !== 1 ? 's' : ''} fully mapped`
                : <span className="flex items-center gap-1 text-amber-600"><AlertTriangle className="h-3.5 w-3.5" /> {notRequired && samples.length === 0 ? 'Add a sample and tag evidence files to it before running' : 'Map evidence files to attributes and samples before running'}</span>}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Close</button>
              {onSaved && (
                <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-xs" role="radiogroup" aria-label="Analysis mode">
                  {[
                    ['fast', 'Fast', 'One AI call per sample covering all attributes. Much quicker; samples whose evidence is too large are checked exhaustively.'],
                    ['exhaustive', 'Exhaustive', 'One AI checking session per sample and attribute, reading the evidence file by file. Slower.'],
                  ].map(([mode, label, hint]) => (
                    <button key={mode} type="button" role="radio" aria-checked={analysisMode === mode} aria-label={label} title={hint}
                      onClick={() => chooseAnalysisMode(mode)}
                      className={`px-3 py-2 ${analysisMode === mode ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
              <button onClick={handleSaveAndRun} disabled={uploading || saving}
                className="inline-flex items-center gap-2 px-5 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                {saving
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</>
                  : onSaved
                    ? <><Sparkles className="h-3.5 w-3.5" /> Save & Run AI Test</>
                    : 'Done'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
